import React, { useState } from 'react';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { addBillingCredit } from '../../api/spotlights';
import { fmtMoney2 } from '../../helpers/spotlights_dashboard_helpers';

// Grant a credit against the brand's upcoming scheduled ad-spend invoices. The next scheduled
// invoice deducts it as a negative line (never past $0 — an unconsumed remainder stays pending and
// rolls to the following invoice). The description is brand-facing: it appears on the Stripe invoice.
const BillingCreditModal = ({ brand, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [approvalReference, setApprovalReference] = useState('');
  const [sending, setSending] = useState(false);

  const amountValid = Number(amount) >= 0.01 && Number(amount) <= 999999.99;
  const approvalValid = /^https:\/\/shopmyshelf\.slack\.com\/archives\/[A-Z0-9]+\/p[0-9]+(?:\?[^\s]*)?$/.test(approvalReference.trim());
  const canSend = amountValid && description.trim().length > 0 && approvalValid && approvalReference.trim().length <= 2048 && !sending;

  const send = async () => {
    if (!canSend) return;
    const confirmed = window.confirm(
      `Add a ${fmtMoney2(Number(amount))} credit for ${brand.brandName}? It will be deducted from their next scheduled ad-spend invoice(s).`
    );
    if (!confirmed) return;
    setSending(true);
    try {
      await addBillingCredit(brand.Brand_id, {
        amount: Number(amount),
        description: description.trim(),
        internalNote: internalNote.trim() || undefined,
        approvalReference: approvalReference.trim()
      });
      cogoToast.success(`Credit added for ${brand.brandName}.`);
      onDone && onDone();
      onClose();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to add the credit.');
      setSending(false);
    }
  };

  return (
    <div className='sl-modal-overlay' onMouseDown={onClose}>
      <div className='sl-modal' style={{ maxWidth: 560 }} onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>Add invoice credit — {brand.brandName}</div>
          <button className='sl-modal-close' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body'>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13 }}>
              <div className='sl-card-label'>Amount</div>
              <input
                type='number'
                min='0.01'
                step='0.01'
                value={amount}
                onChange={e => setAmount(e.target.value)}
                style={{ padding: 8, borderRadius: 8, width: 130, textAlign: 'right' }}
              />
            </label>
            <label style={{ fontSize: 13, flex: 1, minWidth: 260 }}>
              <div className='sl-card-label'>Description (shown to the brand on the Stripe invoice)</div>
              <input
                type='text'
                placeholder='e.g. Credit for July ad spend adjustment'
                value={description}
                maxLength={200}
                onChange={e => setDescription(e.target.value)}
                style={{ padding: 8, borderRadius: 8, width: '100%' }}
              />
            </label>
          </div>

          <label style={{ fontSize: 13, display: 'block', marginTop: 12 }}>
            <div className='sl-card-label'>Internal note (admins only)</div>
            <input
              type='text'
              placeholder='Optional: why this credit was granted'
              value={internalNote}
              onChange={e => setInternalNote(e.target.value)}
              style={{ padding: 8, borderRadius: 8, width: '100%' }}
            />
          </label>

          <label style={{ fontSize: 13, display: 'block', marginTop: 12 }}>
            <div className='sl-card-label'>Finance approval thread</div>
            <input
              type='url'
              required
              maxLength={2048}
              placeholder='Paste the Slack link to Finance’s approval'
              value={approvalReference}
              onChange={e => setApprovalReference(e.target.value)}
              style={{ padding: 8, borderRadius: 8, width: '100%' }}
            />
          </label>

          <div style={{ color: '#9a9a9a', fontSize: 12, marginTop: 12 }}>
            The credit applies automatically to the next scheduled invoice as a negative line item. An invoice never goes below $0 — any remainder
            stays pending and rolls to the following invoice. Pending credits can be cancelled from the billing panel.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button className='sl-btn' disabled={!canSend} onClick={send}>
              {sending ? 'Adding…' : 'Add credit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingCreditModal;
